/* eslint-disable @typescript-eslint/no-inferrable-types */
import { IServiceLanguage } from './../../../../../../core/models/service-language.model';
import { ExtractHttpErrorFieldErrorsUtil } from '../../../../../../core/utils/http-field-error-extractor.utils';
import { IServiceChangeRequest } from '../../../../../../core/models/service-change-request.model';
import { IServiceTemplate } from '../../../../../../core/models/service-template/service-template.model';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, ValidationErrors } from '@angular/forms';
import {
  IUiAlertContent,
  TUiAlertTypes,
  ToastService,
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
} from '@irembo-andela/irembogov3-common';
import { IFieldError } from '../../../../../../core/models/service-change-request-config-steps-forms.model';
import { ServiceChangeRequestService } from '../../../../../../core/services/service-change-request.service';
import { ServiceTemplateService } from '../../../../../../core/services/service-template.service';
import {
  Subscription,
  catchError,
  finalize,
  firstValueFrom,
  forkJoin,
  of,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'irembogov-translation-service-overview',
  templateUrl: './translation-service-overview.component.html',
  styleUrls: ['./translation-service-overview.component.scss'],
})
export class TranslationServiceOverviewComponent
  implements OnDestroy, OnChanges
{
  @Input() changeRequest!: IServiceChangeRequest;
  @Input() defaultTranslations!: Record<string, Record<string, unknown>>;
  @Input() selectedLanguage!: IServiceLanguage;
  @Input() selectedLanguageData!: Record<string, unknown>;
  @Input() isTemplateMode = false;
  @Input() template: IServiceTemplate | null = null;
  @Output() formSubmitted: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() apiTranslations: Record<string, string> = {};
  @Input() serviceLanguages: IServiceLanguage[] = [];

  @ViewChild('errorMessageElement') errorMessageElement!: ElementRef;

  showConfigWorkflowErrorMessage = false;
  jsonEditorValidationErrors: ValidationErrors[] = [];
  initialTranslationsData: any = this.defaultTranslations;
  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };
  isLoading = false;
  subscriptions = new Subscription();
  translationsForm = this.fb.group({
    locale: [],
    translations: this.fb.array([]),
  });

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private serviceTemplateService: ServiceTemplateService,
    private toastService: ToastService,
    private fb: FormBuilder
  ) {}

  get translations() {
    return this.translationsForm.controls['translations'] as FormArray;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['defaultTranslations']?.currentValue) {
      this.defaultTranslations = changes['defaultTranslations'].currentValue;
      this.initialTranslationsData =
        changes['defaultTranslations'].currentValue;
    }

    if (changes['selectedLanguageData']?.currentValue) {
      this.selectedLanguageData = changes['selectedLanguageData'].currentValue;
      this.initializeTranslationForm(this.selectedLanguageData);
    }
  }

  initializeTranslationForm(selectedTranslation: Record<string, unknown>) {
    this.translations.clear();
    for (const [key, value] of Object.entries(selectedTranslation)) {
      const hasApiTranslation = key in this.apiTranslations;
      const translation = this.fb.group({
        word: [key],
        translation: [
          {
            value: hasApiTranslation ? this.apiTranslations[key] : value,
            disabled: hasApiTranslation,
          },
        ],
        isFromApi: [hasApiTranslation],
      });

      this.translations.push(translation);
    }
  }

  async onTranslationsSubmit(): Promise<void> {
    this.isLoading = true;
    if (!this.changeRequest.id && !this.template?.id) {
      this.isLoading = false;
      return;
    }

    this.updateErrorMessageContent(false);
    const translatedWords: Record<string, unknown> = {};
    this.translationsForm.value.translations?.forEach((translation: any) => {
      // Include the translation if it's not from API
      if (!translation.isFromApi) {
        translatedWords[translation.word] = translation.translation;
      } else {
        // For API translations, preserve the existing value
        translatedWords[translation.word] =
          this.apiTranslations[translation.word];
      }
    });
    const translations = await this.buildAllLanguageTranslations(
      translatedWords
    );
    const resultObject = { translations };

    const updateTranslation$ =
      this.isTemplateMode && this.template?.id
        ? this.serviceTemplateService.updateTemplateTranslation(
            this.template.id,
            resultObject
          )
        : this.serviceChangeRequestService.updateServiceChangeRequestTranslation(
            this.changeRequest.id!,
            resultObject
          );

    const updateTranslationSubscription = updateTranslation$
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.toastService.show({
            body: this.isTemplateMode
              ? 'Template translations updated successfully'
              : res.responseMessage,
            type: 'success',
          });
          this.formSubmitted.emit(true);
        },
        error: this.handleError.bind(this),
      });

    this.subscriptions.add(updateTranslationSubscription);
  }

  private async buildAllLanguageTranslations(
    selectedTranslations: Record<string, unknown>
  ): Promise<Record<string, Record<string, unknown>>> {
    const languages = [...this.serviceLanguages];
    if (
      !languages.some(
        language => language.locale === this.selectedLanguage.locale
      )
    ) {
      languages.push(this.selectedLanguage);
    }

    const globalTranslations = (await firstValueFrom(
      forkJoin(
        languages.map(language =>
          this.serviceChangeRequestService
            .getStaticTranslations(language.locale)
            .pipe(catchError(() => of({} as Record<string, string>)))
        )
      )
    )) as Record<string, string>[];
    const existing = this.initialTranslationsData?.translations ?? {};
    const result: Record<string, Record<string, unknown>> = {};
    Object.entries(existing).forEach(([locale, values]) => {
      result[locale] = { ...(values as Record<string, unknown>) };
    });
    const words = this.translations.controls
      .map(control => control.get('word')?.value)
      .filter((word): word is string => typeof word === 'string');

    languages.forEach((language, index) => {
      const localeValues = { ...(result[language.locale] ?? {}) };
      words.forEach(word => {
        if (!(word in localeValues)) {
          localeValues[word] = globalTranslations[index]?.[word] ?? '';
        }
      });
      result[language.locale] = localeValues;
    });
    result[this.selectedLanguage.locale] = {
      ...(result[this.selectedLanguage.locale] ?? {}),
      ...selectedTranslations,
    };
    return result;
  }

  private handleError = (error: HttpErrorResponse): void => {
    const errorMessageAndCode = ExtractHttpErrorResponseCodeAndMessage(
      error,
      `Failed to update ${
        this.isTemplateMode ? 'template' : 'change request'
      } translations`
    );
    const fieldErrors = ExtractHttpErrorFieldErrorsUtil(error);

    this.toastService.show({
      body: errorMessageAndCode.message,
      type: 'error',
    });
    this.updateErrorMessageContent(
      true,
      'danger',
      'Translation Not Updated',
      errorMessageAndCode.message,
      fieldErrors
    );
    this.formSubmitted.emit(false);
  };

  private updateErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title: string = '',
    message: string = '',
    fieldErrors: IFieldError[] | null = null
  ): void {
    this.showConfigWorkflowErrorMessage = show;

    let extraMessage = '';
    if (fieldErrors) {
      extraMessage = fieldErrors
        .map(({ field, errorCode }) => `${field}: ${errorCode}`)
        .join('\n');
    }

    this.formAlertContent = {
      type,
      title,
      message,
      extraMessage,
    };

    if (show) {
      this.scrollErrorMessageIntoView();
    }
  }

  private scrollErrorMessageIntoView(): void {
    this.errorMessageElement.nativeElement.scrollIntoView({
      behavior: 'smooth',
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
